import { HasuraQueries } from "@/api/hasura/queries"
import {
  Install_Codes_Order_By,
  GetInstallCodesQueryVariables,
} from "@/api/hasura/generated/graphql"
import { useQuery } from "@/api/utils/useQuery"
import {
  IInstallCode,
  IInstallCodeExtra,
} from "@/models/installCode/installCode.types"
import {
  InstallCodeExtraParser,
  InstallCodeParser,
} from "@/models/installCode/installCode.parser"
import { nullsFirstInOrder } from "@/api/utils/hasuraHelpers"

export type InstallCodeFilters = Omit<
  GetInstallCodesQueryVariables,
  "search" | "offset" | "limit" | "orderBy"
>

// Relation order-bys aren't sortable columns in the install codes table, and their nested
// shape isn't assignable to ColumnsOrderBy's flat Order_By index signature.
export type InstallCodesOrderBy = Omit<
  Install_Codes_Order_By,
  "meter" | "meter_point"
>

export type InstallCodeQueryResult = {
  records: IInstallCode[]
  totalCount: number
}

export type InstallCodeExtraQueryResult = {
  records: IInstallCodeExtra[]
  totalCount: number
}

export type InstallCodesSummaryQueryResult = {
  totalCount: number
  installCodesCount: number
  missingInstallCodesCount: number
  missingAsnCount: number
  expiringAsnCount: number
  missingAsnMsns: number[]
  expiringAsnMsns: number[]
}

class InstallCodesServiceClass {
  getInstallCodeList(
    search: string,
    page: number,
    pageSize: number,
    orderBy: InstallCodesOrderBy,
    filters: InstallCodeFilters,
  ) {
    if (Object.values(orderBy)[0] === "desc") {
      orderBy = nullsFirstInOrder(orderBy)
    }

    return useQuery({
      ...HasuraQueries.smartMeter.getInstallCodes(
        `%${search}%`,
        (page - 1) * pageSize,
        pageSize,
        orderBy,
        filters,
      ),
      placeholderData: {
        records: [],
        totalCount: 0,
      },
      useRawResponse: true,
      parserFn: (rawData) => ({
        records: rawData.install_codes.map((installCode) =>
          InstallCodeParser.toDomain(installCode),
        ),
        totalCount: rawData.install_codes_aggregate?.aggregate?.count ?? 0,
      }),
    })
  }

  getInstallCodeExtraList(
    search: string,
    page: number,
    pageSize: number,
    orderBy: InstallCodesOrderBy,
    filters: InstallCodeFilters,
  ) {
    if (Object.values(orderBy)[0] === "desc") {
      orderBy = nullsFirstInOrder(orderBy)
    }

    return useQuery({
      ...HasuraQueries.smartMeter.getInstallCodesExtra(
        `%${search}%`,
        (page - 1) * pageSize,
        pageSize,
        orderBy,
        filters,
      ),
      placeholderData: {
        records: [],
        totalCount: 0,
      },
      useRawResponse: true,
      parserFn: (rawData) => ({
        records: rawData.install_codes.map((installCode) =>
          InstallCodeExtraParser.toDomain(installCode),
        ),
        totalCount: rawData.install_codes_aggregate?.aggregate?.count ?? 0,
      }),
    })
  }

  getInstallCodesSummary() {
    return useQuery({
      ...HasuraQueries.smartMeter.getInstallCodesSummary(),
      placeholderData: {
        totalCount: 0,
        installCodesCount: 0,
        missingInstallCodesCount: 0,
        missingAsnCount: 0,
        expiringAsnCount: 0,
        missingAsnMsns: [],
        expiringAsnMsns: [],
      },
      useRawResponse: true,
      parserFn: (rawData) => {
        const totalCount = rawData.total?.aggregate?.count ?? 0
        const installCodesCount =
          rawData.install_codes_count?.aggregate?.count ?? 0

        return {
          totalCount,
          installCodesCount,
          missingInstallCodesCount: totalCount - installCodesCount,
          missingAsnCount: rawData.missing_asn_count?.aggregate?.count ?? 0,
          expiringAsnCount: rawData.expiring_asn_count?.aggregate?.count ?? 0,
          missingAsnMsns: rawData.missing_asn_msns.map((result) => result.msn),
          expiringAsnMsns: rawData.expiring_asn_msns.map(
            (result) => result.msn,
          ),
        }
      },
    })
  }
}

export const InstallCodesService = new InstallCodesServiceClass()
